import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ALLOWED_FILE_SIZE_IN_BYTES, toBase64 } from '@ngsos/portal-core/chat';
import type { AttachmentBody, CallerConnectedStatus } from '@/signalr';
import { NgButton, NgSwitch } from '@/ui';
import { CallerChatConnectionStatusComponent } from './CallerChatConnectionStatusComponent';
import { EmergencyInstructionsDropdown } from './EmergencyInstructionsDropdown';
import { UploadFileButton } from './UploadFileButton';

export type MessageSendPanelProps = {
  callerChatConnectionStatus: CallerConnectedStatus;
  onMessageSend: (message: string) => Promise<void>;
  onMessagePreviewTriggered: (message: string) => Promise<void>;
  onMessageImageSend: (attachment: AttachmentBody) => Promise<void>;
  isChatLoading: boolean;
  incidentIsReadOnly: boolean;
  translateChat: boolean;
  onTranslateChatChanged: (value: boolean) => void | Promise<void>;
  isSendingImage: boolean;
};

export function MessageSendPanel({
  callerChatConnectionStatus,
  onMessageSend,
  onMessagePreviewTriggered,
  onMessageImageSend,
  isChatLoading,
  incidentIsReadOnly,
  translateChat,
  onTranslateChatChanged,
  isSendingImage,
}: MessageSendPanelProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  const messageChanged = async (value: string) => {
    await onMessagePreviewTriggered(value);
  };

  const submitMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (message.trim() !== '') {
      await onMessageSend(message);
      setMessage('');
      await messageChanged('');
    }
  };

  const sendFile = async (file: File) => {
    if (file.size > ALLOWED_FILE_SIZE_IN_BYTES) {
      console.error(
        `Supplied file with size ${file.size} bytes exceeds the maximum of ${ALLOWED_FILE_SIZE_IN_BYTES} bytes.`,
      );
      return;
    }

    const data = new Uint8Array(await file.arrayBuffer());
    const attachment: AttachmentBody = { mimeType: file.type, fileData: toBase64(data) };
    await onMessageImageSend(attachment);
  };

  return (
    <div className="message-send-panel pt-1">
      <form onSubmit={(event) => void submitMessage(event)}>
        {!isChatLoading && (
          <div className="d-flex justify-content-between">
            {incidentIsReadOnly ? (
              <div></div>
            ) : (
              <CallerChatConnectionStatusComponent status={callerChatConnectionStatus} />
            )}

            <NgSwitch
              label={t('Chat_TranslateSwitchLabel')}
              value={translateChat}
              onValueChanged={onTranslateChatChanged}
            />
          </div>
        )}

        <div className="my-1">
          <input
            type="text"
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
              void messageChanged(event.target.value);
            }}
            className="form-control"
            disabled={incidentIsReadOnly}
          />
        </div>

        <div className="d-flex justify-content-between">
          <div className="d-flex gap-1">
            <UploadFileButton onFileChanged={sendFile} disabled={incidentIsReadOnly} isUploading={isSendingImage} />
            <EmergencyInstructionsDropdown disabled={incidentIsReadOnly} />
          </div>
          <div>
            <NgButton
              submitButton
              icon="fal fa-paper-plane-top"
              type="light"
              size="small"
              disabled={incidentIsReadOnly || message.trim() === ''}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
